import type { ShortlistGroup } from '@elara/client-core/models/ShortlistGroupModel';
import type { ShortlistItem } from '@elara/client-core/models/ShortlistItemModel';
import type { RoomShortlistMetadata } from '@elara/domain/models/schemas/shortlist-schemas';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { useChatsState, useMessageViewState, useRouterState, useShortlistState, useTripsState } from '#states/hooks.js';
import { DestinationCard } from '#ui/cards/DestinationCard.js';
import { FlightCard } from '#ui/cards/FlightCard.js';
import { HotelCard } from '#ui/cards/HotelCard.js';
import { PlaceCard } from '#ui/cards/PlaceCard.js';
import { RoomCard } from '#ui/cards/RoomCard.js';

import { ShortlistItemActionsMenu } from './ShortlistItemActionsMenu.js';

import { Card } from '../card.js';

interface ShortlistItemCardContentProps {
  item: ShortlistItem;
  groups: ShortlistGroup[];
  groupsWithItem: Set<string>;
  onNavigateToMessage: () => void;
  onMoveToGroup: (groupId: string) => Promise<void>;
  onCopyToGroup: (groupId: string) => Promise<void>;
  onRemove: () => Promise<void>;
}

function ShortlistItemCardContent({
  item,
  groups,
  groupsWithItem,
  onNavigateToMessage,
  onMoveToGroup,
  onCopyToGroup,
  onRemove,
}: ShortlistItemCardContentProps) {
  const shortlistState = useShortlistState();

  const actions = (
    <ShortlistItemActionsMenu
      item={item}
      groups={groups}
      groupsWithItem={groupsWithItem}
      onNavigateToMessage={onNavigateToMessage}
      onMoveToGroup={onMoveToGroup}
      onCopyToGroup={onCopyToGroup}
      onRemove={onRemove}
    />
  );

  return (
    <div className="relative sl-card">
      {/* Content */}
      {item.isFlight && item.flightOption && (
        <FlightCard flightOption={item.flightOption} actions={actions} showTotals={shortlistState.showTotals} />
      )}
      {item.isDestination && item.destination && <DestinationCard destination={item.destination} actions={actions} />}
      {item.isHotel && item.hotel && <HotelCard hotel={item.hotel} actions={actions} />}
      {item.isPlace && item.place && <PlaceCard place={item.place} actions={actions} />}
      {item.isRoom &&
        item.roomOption &&
        (() => {
          // A pinned item shows its rate; an unpinned one shows the room's lowest bookable rate for the
          // saved party.
          const meta = item.metadata as Partial<RoomShortlistMetadata> | undefined;
          return (
            <RoomCard
              room={item.roomOption}
              actions={actions}
              quantity={meta?.quantity}
              productCode={meta?.productCode}
              occupancy={meta?.occupancy}
              showTotals={shortlistState.showTotals}
            />
          );
        })()}
    </div>
  );
}

interface ShortlistItemCardPlaceholderProps {
  item: ShortlistItem;
  groups?: ShortlistGroup[];
}

/**
 * Placeholder for drop preview that matches the exact height of a card being dragged
 * Used to show where an item will be dropped when dragging between groups.
 * Renders the card structure invisibly to maintain exact height, with a visible border indicator.
 */
export function ShortlistItemCardPlaceholder({ item, groups = [] }: ShortlistItemCardPlaceholderProps) {
  return (
    <Card className="@container overflow-hidden relative opacity-0 pointer-events-none">
      <ShortlistItemCardContent
        item={item}
        groups={groups}
        groupsWithItem={new Set<string>()}
        onNavigateToMessage={() => {}}
        onMoveToGroup={async () => {}}
        onCopyToGroup={async () => {}}
        onRemove={async () => {}}
      />
    </Card>
  );
}

interface ShortlistItemCardProps {
  item: ShortlistItem;
  groups?: ShortlistGroup[];
  className?: string;
  isDragging?: boolean;
  disableSortable?: boolean;
}

export function ShortlistItemCard({
  item,
  groups = [],
  className,
  isDragging = false,
  disableSortable = false,
}: ShortlistItemCardProps) {
  const sortable = useSortable({ id: item.id, disabled: disableSortable });
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = disableSortable
    ? { attributes: {}, listeners: {}, setNodeRef: () => {}, transform: null, transition: undefined, isDragging: false }
    : sortable;

  // When dragging, hide completely in the original position
  // The DragOverlay will show the item following the cursor
  const style = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition: isSortableDragging ? undefined : transition,
    opacity: isSortableDragging ? 0 : 1,
  };
  const shortlistState = useShortlistState();
  const tripsState = useTripsState();
  const routerState = useRouterState();
  const chatsState = useChatsState();
  const messageViewState = useMessageViewState();

  const handleRemove = async () => {
    await shortlistState.removeItem(item.id);
  };

  const handleNavigateToMessage = () => {
    if (!item.messageRef) return;

    const tripId = tripsState.current?.id;
    if (!tripId) return;

    const { chatId, messageId } = item.messageRef;

    // Scroll to message helper
    const scrollToMessage = () => {
      messageViewState.scrollToMessage(messageId);
    };

    // Check if we're on the correct chat
    const currentChatId = chatsState.current?.id;
    const needsChatSwitch = currentChatId !== chatId;

    // If we need to switch chats, navigate to the chat panel path
    if (needsChatSwitch) {
      routerState.navigate(`/trip/${tripId}/chat`);
      // Wait for chat to load after navigation, then scroll
      setTimeout(scrollToMessage, 300);
      return;
    }

    // Check if we need to switch panels (on mobile)
    const needsPanelSwitch = tripsState.isMobile && tripsState.activePanel !== 'chat';

    // If we need to switch panels, use the callback to scroll after switch
    if (needsPanelSwitch) {
      tripsState.switchToPanel('chat', scrollToMessage);
      return;
    }

    // We're on the correct chat and panel, just scroll to the message
    scrollToMessage();
  };

  const handleMoveToGroup = async (groupId: string) => {
    await shortlistState.moveToGroup(item.id, groupId);
  };

  const handleCopyToGroup = async (groupId: string) => {
    await shortlistState.copyToGroup(item.id, groupId);
  };

  const canOpenPreview =
    item.referenceId &&
    ['destination', 'hotel', 'place', 'restaurant', 'experience', 'activity', 'room', 'flight'].includes(item.type);

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't open preview if clicking on buttons or dropdowns
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('[role="menuitem"]') ||
      target.closest('[role="menu"]') ||
      target.closest('a')
    ) {
      return;
    }

    // Only open preview if we have a referenceId
    if (item.referenceId) {
      // Open the appropriate preview based on item type
      switch (item.type) {
        case 'destination': {
          tripsState.showGuide('destination', item.referenceId);
          break;
        }
        case 'hotel': {
          tripsState.showGuide('hotel', item.referenceId);
          break;
        }
        case 'room': {
          tripsState.showGuide('room', item.referenceId!);
          break;
        }
        case 'activity': {
          tripsState.showGuide('place', item.referenceId);
          break;
        }
        case 'flight': {
          tripsState.showGuide('flight', item.referenceId);
          break;
        }
        case 'place': {
          tripsState.showGuide('place', item.referenceId);
          break;
        }
      }
    }

    // If no preview was opened but we have a messageRef, navigate to the message
    if (!canOpenPreview && item.messageRef) {
      handleNavigateToMessage();
    }
  };

  // Find groups that already contain this item (by referenceId and type)
  // Access items directly (it's a signal, so this will trigger re-renders when it changes)
  const items = shortlistState.items;
  const relevantItems = items.filter((i) => i.type === item.type && i.referenceId === item.referenceId);

  const groupsWithItem = React.useMemo(() => {
    if (!item.referenceId) return new Set<string>();
    return new Set(relevantItems.map((i) => i.groupId).filter(Boolean) as string[]);
  }, [item.referenceId, relevantItems]);

  const canClickCard = canOpenPreview || (item.messageRef && !canOpenPreview);

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={cn(
        '@container overflow-hidden shadow-sm group touch-manipulation',
        canClickCard && 'cursor-pointer transition-colors',
        !isSortableDragging && 'cursor-grab active:cursor-grabbing',
        (isDragging || isSortableDragging) && 'shadow-lg',
        className,
      )}
      onClick={canClickCard ? handleCardClick : undefined}
      {...attributes}
      {...listeners}
    >
      <ShortlistItemCardContent
        item={item}
        groups={groups}
        groupsWithItem={groupsWithItem}
        onNavigateToMessage={handleNavigateToMessage}
        onMoveToGroup={handleMoveToGroup}
        onCopyToGroup={handleCopyToGroup}
        onRemove={handleRemove}
      />
    </Card>
  );
}
